import { useQuery } from '@tanstack/react-query';
import { type ReactElement, useMemo, useState } from 'react';
import { CoverageUploadAction } from '@/features/codeCoverage/components/CoverageUploadAction';
import { queryCoverageFiles } from '@/features/codeCoverage/data/coverageFiles';
import { queryCoverageRuns } from '@/features/codeCoverage/data/coverageRuns';
import {
  buildCoverageArtifactRows,
  buildCoverageTableDateRangeLabel,
} from '@/features/codeCoverage/pages/coverageArtifactRows';
import {
  resolveCoverageMetricCardIcon,
  resolveCoverageMetricCardIconColor,
} from '@/features/codeCoverage/pages/coverageMetricCardIcons';
import { CoveragePageFiltersToolbar } from '@/features/codeCoverage/pages/CoveragePageFiltersToolbar';
import { CoverageRunsHistoryTable } from '@/features/codeCoverage/pages/CoverageRunsHistoryTable';
import {
  COVERAGE_CHART_PAGE_SIZE,
  resolveCoverageComparisonWindowLabel,
  toDateEndIso,
  toDateStartIso,
} from '@/features/codeCoverage/pages/coverageRunsPage.constants';
import {
  buildCoverageRunOptions,
  buildCoverageDirectoryOptions,
  buildCoverageFileOptions,
  buildCoverageTagOptions,
} from '@/features/codeCoverage/pages/coverageRunsPageFilterOptions';
import { CoverageTablesPanel } from '@/features/codeCoverage/pages/CoverageTablesPanel';
import { useCoverageTablesState } from '@/features/codeCoverage/pages/useCoverageTablesState';
import { useCoverageRefreshVersion } from '@/features/codeCoverage/state/coverageRefreshSignal';
import { StatusMetricCardsRow } from '@/features/metrics/components/StatusMetricCardsRow';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects/hooks/projectHooks';
import { useProjectMetricThresholds } from '@/features/projects/hooks/useProjectMetricThresholds';
import { formatRangeWindowNoDataText } from '@/features/quality/hooks/useQualityDateRange';
import { DeferredRenderBlock } from '@/features/shared/components/deferred/DeferredRenderBlock';
import { type DateQuickFilter, useDateWindow } from '@/features/shared/hooks/useDateWindow';
import { queryUploads } from '@/features/uploads/data';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { useApiErrorMessage } from '@/shared/data/errors';
import { queryKeys } from '@/shared/hooks';
import { s } from '@/theme/tokens';
import { Box, Stack, StatusBanner, Text } from '@/ui';
import {
  buildDateRangeFilters,
  buildEqFilter,
  buildInFilter,
  buildProjectScopedFilters,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { ListPageTemplate } from '@/ui/composites/scaffolds';
import { layoutModeTokens, useLayoutMode } from '@/ui/layouts/layoutMode';
import { MAX_PAGE_SIZE as QUERY_MAX_PAGE_SIZE } from '@flowqa/shared';
import { buildCoverageMetricPanels } from './coverageMetricPanels';
import {
  buildCoverageRunMetricCards,
  buildCoverageRunSummaryFromRuns,
} from './coverageRunsPageModel';
import { CoverageTimelineChartPanel } from './CoverageTimelineChartPanel';
import type { CoverageFileDto, CoverageRunDto, QueryResult, UploadDto } from '@flowqa/shared';
import type { Dayjs } from 'dayjs';

const ALLOWED_UPLOAD_FILTER_FIELDS = new Set([
  'source',
  'fileName',
  'contentType',
  'idempotencyKey',
  'runKey',
  'purpose',
  'status',
  'projectId',
  'createdById',
  'createdAt',
]);

const COVERAGE_HISTORY_PAGE_SIZE = QUERY_MAX_PAGE_SIZE;
type CoverageFilesQueryInput = Parameters<typeof queryCoverageFiles>[0];
type UploadFilter = NonNullable<Parameters<typeof queryUploads>[0]['filters']>[number];

const queryAllCoverageFiles = async (
  input: Omit<CoverageFilesQueryInput, 'page' | 'pageSize'>,
  orgId?: string | null,
): Promise<QueryResult<CoverageFileDto>> => {
  const firstPage = await queryCoverageFiles(
    {
      ...input,
      page: 1,
      pageSize: COVERAGE_HISTORY_PAGE_SIZE,
    },
    orgId,
  );

  if (firstPage.total <= firstPage.items.length) {
    return firstPage;
  }

  const items = [...firstPage.items];
  let page = 2;

  while (items.length < firstPage.total) {
    const nextPage = await queryCoverageFiles(
      {
        ...input,
        page,
        pageSize: COVERAGE_HISTORY_PAGE_SIZE,
      },
      orgId,
    );
    if (nextPage.items.length === 0) {
      break;
    }
    items.push(...nextPage.items);
    page += 1;
  }

  return {
    ...firstPage,
    items,
    page: 1,
    pageSize: items.length,
  };
};

const enforceCoverageUploadPurposeFilter = (filters: UploadFilter[]): UploadFilter[] =>
  mergeFilters(
    filters.filter((filter) => filter.field !== 'purpose'),
    buildEqFilter('purpose', 'coverage'),
  );

export const CoverageRunsPage = (): ReactElement => {
  const layoutMode = useLayoutMode();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const { thresholds } = useProjectMetricThresholds(selectedProjectId);
  const coverageRefreshVersion = useCoverageRefreshVersion();
  const toErrorMessage = useApiErrorMessage();
  const [dateQuickFilter, setDateQuickFilter] = useState<DateQuickFilter>('7d');
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [filterCoverageRuns, setFilterCoverageRuns] = useState<string[]>([]);
  const [filterDirectories, setFilterDirectories] = useState<string[]>([]);
  const [filterFiles, setFilterFiles] = useState<string[]>([]);
  const [filterTags, setFilterTags] = useState<string[]>([]);
  const {
    activeTableTab,
    coverageFilesColumnVisibilityModel,
    coverageFilesSortDirection,
    coverageFilesSortKey,
    coverageRunsColumnVisibilityModel,
    coverageRunsSortDirection,
    coverageRunsSortKey,
    onCoverageFilesSortChange,
    onCoverageRunsSortChange,
    onUploadsSortChange,
    setActiveTableTab,
    setCoverageFilesColumnVisibilityModel,
    setCoverageRunsColumnVisibilityModel,
    setUploadsColumnVisibilityModel,
    uploadsColumnVisibilityModel,
    uploadsSortDirection,
    uploadsSortKey,
    uploadsToolbarQueryKey,
  } = useCoverageTablesState();
  const dateWindow = useDateWindow({
    dateQuickFilter,
    fromDate,
    toDate,
  });
  const baseFilters = useMemo(
    () =>
      mergeFilters(
        buildProjectScopedFilters(selectedProjectId),
        buildDateRangeFilters(
          'capturedAt',
          toDateStartIso(dateWindow.startDay),
          toDateEndIso(dateWindow.endDay),
        ),
      ),
    [dateWindow.endDay, dateWindow.startDay, selectedProjectId],
  );
  const coverageRunFilters = useMemo(
    () => mergeFilters(baseFilters, buildInFilter('tags', filterTags)),
    [baseFilters, filterTags],
  );
  const filtersKey = useMemo(
    () =>
      [
        selectedProjectId ?? 'all',
        dateQuickFilter,
        dateWindow.startDay ?? 'none',
        dateWindow.endDay ?? 'none',
        filterCoverageRuns.slice().sort().join(',') || 'all-runs',
        filterTags.slice().sort().join(',') || 'all-tags',
        `refresh-${String(coverageRefreshVersion)}`,
      ].join(':'),
    [
      dateQuickFilter,
      dateWindow.endDay,
      dateWindow.startDay,
      filterCoverageRuns,
      filterTags,
      coverageRefreshVersion,
      selectedProjectId,
    ],
  );
  const coverageUploadsFilters = useMemo(
    () =>
      mergeFilters(
        buildProjectScopedFilters(selectedProjectId),
        buildDateRangeFilters(
          'createdAt',
          toDateStartIso(dateWindow.startDay),
          toDateEndIso(dateWindow.endDay),
        ),
        buildEqFilter('purpose', 'coverage'),
      ),
    [dateWindow.endDay, dateWindow.startDay, selectedProjectId],
  );
  const coverageFileFilters = useMemo(
    () =>
      mergeFilters(
        baseFilters,
        buildInFilter('coverageRunId', filterCoverageRuns),
        buildInFilter('parentPath', filterDirectories),
        buildInFilter('fileName', filterFiles),
      ),
    [baseFilters, filterCoverageRuns, filterDirectories, filterFiles],
  );
  const coverageFilesFiltersKey = useMemo(
    () =>
      [
        filtersKey,
        filterCoverageRuns.slice().sort().join(',') || 'all-runs',
        filterDirectories.slice().sort().join(',') || 'all-directories',
        filterFiles.slice().sort().join(',') || 'all-files',
        `refresh-${String(coverageRefreshVersion)}`,
      ].join(':'),
    [coverageRefreshVersion, filterCoverageRuns, filterDirectories, filterFiles, filtersKey],
  );
  const coverageRunsTableDataQueryKey = useMemo(
    () =>
      [
        ...queryKeys.coverageRuns(selectedOrganisationId ?? null, selectedProjectId, filtersKey),
        coverageRunsSortKey,
        coverageRunsSortDirection,
      ] as const,
    [
      coverageRunsSortDirection,
      coverageRunsSortKey,
      filtersKey,
      selectedOrganisationId,
      selectedProjectId,
    ],
  );
  const coverageUploadsDataQueryKey = queryKeys.coverageUploads(
    selectedOrganisationId ?? null,
    selectedProjectId,
    filtersKey,
  );
  const coverageArtifactsTableQueryKey = useMemo(
    () =>
      [...coverageUploadsDataQueryKey, coverageFilesSortKey, coverageFilesSortDirection] as const,
    [coverageFilesSortDirection, coverageFilesSortKey, coverageUploadsDataQueryKey],
  );

  const coverageRunsQuery = useQuery<QueryResult<CoverageRunDto>>({
    queryKey: queryKeys.coverageRuns(selectedOrganisationId ?? null, selectedProjectId, filtersKey),
    queryFn: async () =>
      queryCoverageRuns(
        {
          page: 1,
          pageSize: COVERAGE_CHART_PAGE_SIZE,
          filters: coverageRunFilters,
        },
        selectedOrganisationId ?? null,
      ),
  });
  const recentRuns = coverageRunsQuery.data?.items ?? [];
  const shouldLoadCoverageUploads = activeTableTab === 'coverage-files';
  const coverageUploadsQuery = useQuery<QueryResult<UploadDto>>({
    queryKey: coverageUploadsDataQueryKey,
    queryFn: async () =>
      await queryUploads(
        {
          page: 1,
          pageSize: 100,
          filters: coverageUploadsFilters,
        },
        selectedOrganisationId ?? null,
      ),
    enabled: shouldLoadCoverageUploads,
  });
  const recentUploads = coverageUploadsQuery.data?.items ?? [];
  const selectedRecentRuns = useMemo(
    () =>
      filterCoverageRuns.length === 0
        ? recentRuns
        : recentRuns.filter((run) => filterCoverageRuns.includes(run.id)),
    [filterCoverageRuns, recentRuns],
  );
  const selectedCoverageRunIds = useMemo(
    () => selectedRecentRuns.map((run) => run.id),
    [selectedRecentRuns],
  );
  const shouldLoadCoverageFileOptions =
    activeTableTab === 'coverage-files' || filterDirectories.length > 0 || filterFiles.length > 0;
  const coverageFileOptionsQuery = useQuery<QueryResult<CoverageFileDto>>({
    queryKey: queryKeys.coverageFiles(
      selectedOrganisationId ?? null,
      selectedProjectId,
      `${filtersKey}:filter-options:${filterCoverageRuns.slice().sort().join(',') || 'all-runs'}`,
    ),
    queryFn: async () =>
      queryAllCoverageFiles(
        {
          filters: mergeFilters(
            baseFilters,
            buildInFilter(
              'coverageRunId',
              filterCoverageRuns.length === 0 ? [] : selectedCoverageRunIds,
            ),
          ),
          sorts: [{ field: 'normalizedPath', direction: 'asc' }],
        },
        selectedOrganisationId ?? null,
      ),
    enabled: shouldLoadCoverageFileOptions,
  });
  const filteredUploadIds = useMemo(
    () =>
      selectedRecentRuns
        .map((run) => run.uploadId)
        .filter((uploadId): uploadId is string => Boolean(uploadId)),
    [selectedRecentRuns],
  );
  const coverageFileOptions = coverageFileOptionsQuery.data?.items ?? [];
  const selectedRecentUploads = useMemo(
    () => recentUploads.filter((upload) => filteredUploadIds.includes(upload.id)),
    [filteredUploadIds, recentUploads],
  );
  const coverageArtifacts = useMemo(
    () => buildCoverageArtifactRows(selectedRecentUploads, selectedRecentRuns),
    [selectedRecentRuns, selectedRecentUploads],
  );
  const coverageRunsTableDataKey = useMemo(() => {
    const firstRunId = selectedRecentRuns[0]?.id ?? 'none';
    const lastRunId = selectedRecentRuns[selectedRecentRuns.length - 1]?.id ?? 'none';
    return `${String(selectedRecentRuns.length)}:${firstRunId}:${lastRunId}`;
  }, [selectedRecentRuns]);
  const coverageArtifactsTableDataKey = useMemo(() => {
    const firstArtifactId = coverageArtifacts[0]?.id ?? 'none';
    const lastArtifactId = coverageArtifacts[coverageArtifacts.length - 1]?.id ?? 'none';
    return `${String(coverageArtifacts.length)}:${firstArtifactId}:${lastArtifactId}`;
  }, [coverageArtifacts]);
  const summary =
    selectedRecentRuns.length > 0 ? buildCoverageRunSummaryFromRuns(selectedRecentRuns) : null;
  const previousSummary = null;
  const isLoading = coverageRunsQuery.isLoading;
  const showCoverageChartErrorState = coverageRunsQuery.isError;
  const error =
    coverageRunsQuery.error ??
    (shouldLoadCoverageFileOptions ? coverageFileOptionsQuery.error : null) ??
    (shouldLoadCoverageUploads ? coverageUploadsQuery.error : null);
  const hasCoverageData = recentRuns.length > 0 || recentUploads.length > 0 || summary !== null;
  const errorMessage = error ? toErrorMessage(error) : null;
  const coverageTableDateRangeLabel = useMemo(
    () => buildCoverageTableDateRangeLabel(dateWindow.startDay, dateWindow.endDay),
    [dateWindow.endDay, dateWindow.startDay],
  );
  const metricCards = useMemo(
    () => buildCoverageRunMetricCards(summary, previousSummary),
    [previousSummary, summary],
  );
  const comparisonWindowLabel = useMemo(
    () => resolveCoverageComparisonWindowLabel(dateQuickFilter),
    [dateQuickFilter],
  );
  const coverageChartEmptyTitle = useMemo(
    () =>
      dateWindow.hasCustomBounds
        ? getEmptyStateMessage('entity.noneForSelectedRange', { entity: 'coverageRun' })
        : formatRangeWindowNoDataText(
            getEmptyStateMessage('entity.noneFound', { entity: 'coverageRun' }),
            dateQuickFilter,
            false,
          ),
    [dateQuickFilter, dateWindow.hasCustomBounds],
  );
  const coverageChartColumns =
    layoutMode === layoutModeTokens.compact ? '1fr' : 'repeat(2, minmax(0, 1fr))';
  const metricPanels = useMemo(
    () =>
      buildCoverageMetricPanels({
        runs: selectedRecentRuns,
        summary,
        previousSummary,
        thresholds: thresholds.coverageRuns,
        startDay: dateWindow.startDay,
        endDay: dateWindow.endDay,
      }),
    [
      dateWindow.endDay,
      dateWindow.startDay,
      previousSummary,
      selectedRecentRuns,
      summary,
      thresholds.coverageRuns,
    ],
  );
  const lineCoveragePanel = metricPanels.find((metric) => metric.id === 'lines') ?? null;
  const functionCoveragePanel = metricPanels.find((metric) => metric.id === 'functions') ?? null;
  const branchCoveragePanel = metricPanels.find((metric) => metric.id === 'branches') ?? null;
  const statementCoveragePanel = metricPanels.find((metric) => metric.id === 'statements') ?? null;
  const lineCoverageColor = resolveCoverageMetricCardIconColor('primary');
  const functionCoverageColor = resolveCoverageMetricCardIconColor('info');
  const branchCoverageColor = resolveCoverageMetricCardIconColor('warning');
  const statementCoverageColor = resolveCoverageMetricCardIconColor('success');
  const directoryOptions = useMemo(
    () => buildCoverageDirectoryOptions(coverageFileOptions),
    [coverageFileOptions],
  );
  const coverageRunOptions = useMemo(() => buildCoverageRunOptions(recentRuns), [recentRuns]);
  const fileOptions = useMemo(
    () => buildCoverageFileOptions(coverageFileOptions),
    [coverageFileOptions],
  );
  const tagOptions = useMemo(() => buildCoverageTagOptions(recentRuns), [recentRuns]);
  const coverageRunsLoading = coverageRunsQuery.isLoading;
  const coverageFilesLoading =
    coverageRunsLoading || (shouldLoadCoverageUploads && coverageUploadsQuery.isLoading);
  const coverageHistoryLoading = coverageRunsLoading;
  const shouldRenderCoverageTimelineCharts = isLoading || showCoverageChartErrorState;
  const showCoverageCharts =
    shouldRenderCoverageTimelineCharts ||
    lineCoveragePanel !== null ||
    functionCoveragePanel !== null ||
    branchCoveragePanel !== null ||
    statementCoveragePanel !== null;

  return (
    <ListPageTemplate
      headerActionsMode="separate"
      title={
        <Text role="pageTitle" component="h1">
          Coverage Runs
        </Text>
      }
      titleActions={<CoverageUploadAction />}
      headerActions={
        <CoveragePageFiltersToolbar
          coverageRunOptions={coverageRunOptions}
          dateQuickFilter={dateQuickFilter}
          filterCoverageRuns={filterCoverageRuns}
          directoryOptions={directoryOptions}
          fileOptions={fileOptions}
          filterTags={filterTags}
          filterDirectories={filterDirectories}
          filterFiles={filterFiles}
          fromDate={fromDate}
          onFilterTagsChange={setFilterTags}
          onDateQuickFilterChange={setDateQuickFilter}
          onFilterCoverageRunsChange={setFilterCoverageRuns}
          onFilterDirectoriesChange={setFilterDirectories}
          onFilterFilesChange={setFilterFiles}
          onFromDateChange={setFromDate}
          onToDateChange={setToDate}
          tagOptions={tagOptions}
          toDate={toDate}
        />
      }
      summary={
        <Stack gap="md">
          {errorMessage && !hasCoverageData ? (
            <StatusBanner tone="red" variant="outlined" message={errorMessage} />
          ) : null}
          <StatusMetricCardsRow
            cards={metricCards}
            loading={isLoading}
            skeletonCount={4}
            comparisonWindowLabel={comparisonWindowLabel}
            resolveStatusCardIcon={resolveCoverageMetricCardIcon}
            resolveStatusCardIconColor={resolveCoverageMetricCardIconColor}
          />
        </Stack>
      }
      charts={
        showCoverageCharts ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: coverageChartColumns,
              gap: s('xl'),
            }}
          >
            {shouldRenderCoverageTimelineCharts || lineCoveragePanel ? (
              <CoverageTimelineChartPanel
                title="Line Coverage Timeline"
                data={lineCoveragePanel?.lineTrendData ?? []}
                seriesLabel="Line Coverage"
                color={lineCoverageColor}
                summary={lineCoveragePanel?.summary ?? null}
                loading={isLoading}
                emptyTitle={coverageChartEmptyTitle}
                showErrorState={showCoverageChartErrorState}
              />
            ) : null}
            {shouldRenderCoverageTimelineCharts || functionCoveragePanel ? (
              <CoverageTimelineChartPanel
                title="Function Coverage Timeline"
                data={functionCoveragePanel?.lineTrendData ?? []}
                seriesLabel="Function Coverage"
                color={functionCoverageColor}
                summary={functionCoveragePanel?.summary ?? null}
                loading={isLoading}
                emptyTitle={coverageChartEmptyTitle}
                showErrorState={showCoverageChartErrorState}
              />
            ) : null}
            {shouldRenderCoverageTimelineCharts || branchCoveragePanel ? (
              <CoverageTimelineChartPanel
                title="Branch Coverage Timeline"
                data={branchCoveragePanel?.lineTrendData ?? []}
                seriesLabel="Branch Coverage"
                color={branchCoverageColor}
                summary={branchCoveragePanel?.summary ?? null}
                loading={isLoading}
                emptyTitle={coverageChartEmptyTitle}
                showErrorState={showCoverageChartErrorState}
              />
            ) : null}
            {shouldRenderCoverageTimelineCharts || statementCoveragePanel ? (
              <CoverageTimelineChartPanel
                title="Statement Coverage Timeline"
                data={statementCoveragePanel?.lineTrendData ?? []}
                seriesLabel="Statement Coverage"
                color={statementCoverageColor}
                summary={statementCoveragePanel?.summary ?? null}
                loading={isLoading}
                emptyTitle={coverageChartEmptyTitle}
                showErrorState={showCoverageChartErrorState}
              />
            ) : null}
          </Box>
        ) : null
      }
      content={
        <Stack gap="2xl">
          <CoverageTablesPanel
            activeTableTab={activeTableTab}
            coverageFilesColumnVisibilityModel={coverageFilesColumnVisibilityModel}
            coverageFilesLoading={coverageFilesLoading}
            coverageFilesSortDirection={coverageFilesSortDirection}
            coverageFilesSortKey={coverageFilesSortKey}
            coverageRunsLoading={coverageRunsLoading}
            coverageRunsColumnVisibilityModel={coverageRunsColumnVisibilityModel}
            coverageRunsSortDirection={coverageRunsSortDirection}
            coverageRunsSortKey={coverageRunsSortKey}
            dateQuickFilter={dateQuickFilter}
            endDay={dateWindow.endDay}
            files={coverageArtifacts}
            filesQueryKey={[...coverageArtifactsTableQueryKey, coverageArtifactsTableDataKey]}
            fromDate={fromDate}
            onCoverageFilesColumnVisibilityModelChange={setCoverageFilesColumnVisibilityModel}
            onCoverageFilesSortChange={onCoverageFilesSortChange}
            onCoverageRunsColumnVisibilityModelChange={setCoverageRunsColumnVisibilityModel}
            onCoverageRunsSortChange={onCoverageRunsSortChange}
            onDateQuickFilterChange={setDateQuickFilter}
            onFromDateChange={setFromDate}
            onTableTabChange={setActiveTableTab}
            onToDateChange={setToDate}
            onUploadsColumnVisibilityModelChange={setUploadsColumnVisibilityModel}
            onUploadsSortChange={onUploadsSortChange}
            runs={selectedRecentRuns}
            runsQueryKey={[...coverageRunsTableDataQueryKey, coverageRunsTableDataKey]}
            selectedProjectId={selectedProjectId}
            startDay={dateWindow.startDay}
            toDate={toDate}
            uploadsColumnVisibilityModel={uploadsColumnVisibilityModel}
            uploadsFilters={coverageUploadsFilters}
            uploadsQueryFn={async (input) => {
              const sanitizedInput = {
                ...input,
                filters: enforceCoverageUploadPurposeFilter(
                  input.filters.filter((filter) => ALLOWED_UPLOAD_FILTER_FIELDS.has(filter.field)),
                ),
              };
              const result = await queryUploads(
                {
                  ...sanitizedInput,
                  includeSummary: true,
                  includeSummaryDetails: false,
                },
                selectedOrganisationId ?? null,
              );

              if (filterCoverageRuns.length === 0) {
                return result;
              }

              const filteredItems = result.items.filter((upload) =>
                filteredUploadIds.includes(upload.id),
              );

              return {
                ...result,
                items: filteredItems,
                total: filteredItems.length,
              };
            }}
            uploadsQueryKey={uploadsToolbarQueryKey}
            uploadsSortDirection={uploadsSortDirection}
            uploadsSortKey={uploadsSortKey}
          />
          <DeferredRenderBlock minHeight="22rem" rootMargin="0px 0px">
            <CoverageRunsHistoryTable
              dateRangeLabel={coverageTableDateRangeLabel}
              endDay={dateWindow.endDay}
              filters={
                filterCoverageRuns.length === 0
                  ? coverageFileFilters
                  : mergeFilters(
                      baseFilters,
                      buildInFilter('coverageRunId', selectedCoverageRunIds),
                      buildInFilter('parentPath', filterDirectories),
                      buildInFilter('fileName', filterFiles),
                    )
              }
              loading={coverageHistoryLoading}
              organisationId={selectedOrganisationId ?? null}
              projectId={selectedProjectId}
              queryKeyScope={coverageFilesFiltersKey}
              startDay={dateWindow.startDay}
            />
          </DeferredRenderBlock>
        </Stack>
      }
    />
  );
};
